<!-- Hero Section -->
<div class="relative w-full h-[500px] flex flex-col overflow-hidden bg-cover bg-[100%_35%]" 
    style="background-image: linear-gradient(180deg, rgba(10, 20, 21, 0.85) 0%, rgba(10, 20, 21, 0.55) 30%, rgba(10, 20, 21, 0.25) 70%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, rgba(10, 20, 21, 0.85) 0%, rgba(255, 255, 255, 0) 75%), url('/assets/banner-v2.webp');">
  
  <!-- Decorative orbs with floating animation -->
  <svg class="absolute w-[520px] h-[520px] md:w-[400px] md:h-[400px] top-[-160px] left-[-120px] pointer-events-none opacity-90 blur-[0.5px]" 
       style="animation: float-gentle 20s ease-in-out infinite;"
       viewBox="0 0 400 400" aria-hidden="true" focusable="false">
    <defs>
      <radialGradient id="teamProfileOrbA" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#75b6e7" stop-opacity="0.6"/>
        <stop offset="100%" stop-color="#75b6e7" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <circle cx="200" cy="200" r="200" fill="url(#teamProfileOrbA)"/>
  </svg>
  <svg class="absolute w-[520px] h-[520px] md:w-[400px] md:h-[400px] bottom-[-180px] right-[-160px] pointer-events-none opacity-75 blur-[0.5px]" 
       style="animation: float-gentle 25s ease-in-out infinite 5s;"
       viewBox="0 0 400 400" aria-hidden="true" focusable="false">
    <defs>
      <radialGradient id="teamProfileOrbB" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#ffffff" stop-opacity="0.35"/>
        <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <circle cx="200" cy="200" r="200" fill="url(#teamProfileOrbB)"/>
  </svg>
  
  <!-- Wave separator -->
  <svg class="absolute left-0 right-0 bottom-[-40px] md:bottom-[-55px] w-full h-[80px] md:h-[120px] block pointer-events-none" 
       viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true" focusable="false">
    <path d="M0,40 C240,100 480,0 720,40 C960,80 1200,20 1440,60 L1440,120 L0,120 Z" fill="#f6f6f6"></path>
  </svg>
</div>

<!-- Title Container -->
<div class="title-container-1">
  <div class="title-container-2">
    @if (detail() && !isLoading()) {
      <div class="search-text">Profiel</div>
    }
    @if (isLoading()) {
      <div class="search-text">Team Profiel</div>
    }
  </div>
</div>

<!-- Loading State -->
@if (isLoading()) {
  <app-team-profile-skeleton></app-team-profile-skeleton>
}

<!-- Error State -->
@if (errorMessage() && !isLoading()) {
  <app-error-state 
    [title]="'Teamlid niet gevonden'"
    [message]="errorMessage()"
    [buttonText]="'Terug naar team'"
    (buttonClick)="goBack()">
  </app-error-state>
}

<!-- Member Profile Content -->
@if (detail() && !isLoading()) {
  <div class="relative w-full -mt-[350px] sm:-mt-[200px] z-[1] pb-20 md:pb-24">
    <div class="max-w-[1200px] mx-auto px-[16px] md:px-6">
      <article class="bg-white rounded-[16px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden transition-all duration-700 ease-out opacity-0 translate-y-12 animate-on-scroll">
      
      <!-- Back Button -->
      <div class="px-6 md:px-10 pt-8 md:pt-10 section-fade-in">
        <button class="inline-flex items-center gap-2 text-[#0071e3] hover:text-[#0077ed] font-medium text-base transition-all duration-300 group" 
                (click)="goBack()" 
                aria-label="Terug naar team overzicht">
          <icon name="navigate_next" class="text-[#0071e3] group-hover:text-[#0077ed] transition-all duration-300 group-hover:-translate-x-1.5 group-hover:scale-110" style="transform: rotate(180deg);"></icon>
          <span class="transition-transform duration-300 group-hover:translate-x-0.5">Terug naar team</span>
        </button>
      </div>

      <!-- Profile Header - Identity Banner -->
      <section class="px-6 md:px-10 py-10 md:py-12 section-fade-in">
        <div class="flex flex-col md:flex-row gap-6 md:gap-10 items-center md:items-center">
          
          <!-- Photo -->
          <div class="flex-shrink-0 w-48 md:w-56 lg:w-64 photo-reveal">
            @if (detail()?.photo) {
              <div class="w-full aspect-square rounded-[16px] overflow-hidden shadow-[0_6px_20px_rgba(0,0,0,0.1)] transition-all duration-500 hover:shadow-[0_12px_32px_rgba(0,0,0,0.15)] hover:scale-[1.02]">
                <img
                  [src]="detail()!.photo!.url"
                  [alt]="detail()!.photo!.alternativeText || detail()!.name"
                  class="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
              </div>
            }
            
            <!-- Placeholder if no photo -->
            @if (!detail()?.photo) {
              <div class="w-full aspect-square rounded-[16px] overflow-hidden shadow-[0_6px_20px_rgba(0,0,0,0.1)] bg-gradient-to-br from-[#0071e3] to-[#0090ff] flex items-center justify-center transition-all duration-500 hover:shadow-[0_12px_32px_rgba(0,113,227,0.25)] hover:scale-[1.02]">
                <icon name="person" class="!text-[120px] text-white opacity-50"></icon>
              </div>
            }
          </div>

          <!-- Name, Role, Department -->
          <div class="flex-1 min-w-0 space-y-3">
            <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight animate-on-scroll visible">
              {{ detail()!.name }}
            </h1>
            <div class="space-y-1.5 animate-on-scroll visible" style="transition-delay: 0.1s;">
              @if (detail()?.role) {
                <p class="text-xl md:text-2xl font-semibold text-[#0071e3]">
                  {{ detail()!.role }}
                </p>
              }
              @if (detail()?.department) {
                <p class="text-base md:text-lg text-gray-600 font-medium">
                  {{ detail()!.department }}
                </p>
              }
            </div>
          </div>
          
        </div>
      </section>

      <!-- Short Bio Section -->
      @if (detail()?.shortBio) {
        <section class="px-6 md:px-10 py-8 md:py-10 section-fade-in">
          <p class="text-2xl md:text-3xl text-gray-700 leading-relaxed font-medium max-w-4xl">
            {{ detail()!.shortBio }}
          </p>
        </section>
      }

      <!-- Full Bio Section -->
      @if (detail()?.fullBio) {
        <section class="px-6 md:px-10 py-8 md:py-10 border-t border-gray-100 section-fade-in">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Over {{ detail()!.name }}
          </h2>
          <p class="text-lg text-gray-700 leading-relaxed whitespace-pre-line max-w-4xl">
            {{ detail()!.fullBio }}
          </p>
        </section>
      }

      <!-- Expertise Section -->
      @if (detail()?.expertise && detail()!.expertise!.length > 0) {
        <section class="px-6 md:px-10 py-8 md:py-10 border-t border-gray-100 section-fade-in">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Expertise</h2>
          <div class="flex flex-wrap gap-2.5">
            @for (skill of detail()!.expertise; track skill) {
              <span class="pill-stagger inline-flex items-center px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100 hover:bg-blue-100 hover:border-blue-200 hover:shadow-md hover:scale-105 transition-all duration-300 shadow-sm cursor-default">
                {{ skill }}
              </span>
            }
          </div>
        </section>
      }

      <!-- Contact Section -->
      @if (detail()?.email || detail()?.phone || detail()?.linkedin || detail()?.whatsapp) {
        <section class="px-6 md:px-10 py-8 md:py-10 pb-10 md:pb-12 border-t border-gray-100 section-fade-in">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Contact</h2>
          <app-social-links
            [email]="detail()?.email"
            [phone]="detail()?.phone"
            [linkedin]="detail()?.linkedin"
            [whatsapp]="detail()?.whatsapp"
            [personName]="detail()!.name"
            [showEmail]="true"
            [showPhone]="true"
            [showLinkedIn]="true"
            [showWhatsApp]="true"
            [showFacebook]="false"
            [showTwitter]="false"
            [showCopyLink]="false"
          ></app-social-links>
        </section>
      }

      </article>
    </div>
  </div>
}
